import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  cancelJob as apiCancelJob,
  fetchJob,
  fetchJobs,
  isJobsApiAvailable,
  jobHasWarnings,
  markJobsSeen,
  openJobEventStream,
  retryJob as apiRetryJob,
  type JobEvent,
  type JobRecord,
} from '../api/jobsApi';
import { useAuth } from '../auth/AuthProvider';

const MAX_JOBS = 30;
const POLL_FALLBACK_MS = 5000;
const RECONNECT_BASE_MS = 2000;
const RECONNECT_MAX_MS = 30000;
const ACTIVE_STATUSES = new Set(['queued', 'running', 'retrying', 'recovering']);

export type JobProgress = { progressPct: number | null; etaSeconds: number | null; message: string | null };

/** Per-step outcomes accumulated from the job's narrated progress feed. */
export type JobStepOutcomes = Record<string, 'done' | 'failed'>;

// The backend narrates each prepare step as "<label> — done|failed"
// (dt_core_job_types._PREPARE_STEP_LABELS). Parsing that narration is what
// lets the drawer draw a live pipeline instead of a bare spinner. The reverse
// map must match the backend labels exactly; an unrecognized message simply
// doesn't advance the pipeline (fail-open to the old behavior).
const STEP_LABEL_TO_KEY: Record<string, string> = {
  'Seeding the twin from the chart': 'seed',
  'Scoring organ health': 'organs',
  'Reading social screening records': 'sdoh',
  'Reading reported family history': 'family',
  'Writing condition narratives': 'trajectory',
  'Writing the patient summary': 'summary',
};

function parseStepMessage(message: string | null): { step: string; outcome: 'done' | 'failed' } | null {
  if (!message) return null;
  const m = /^(.*) — (done|failed)$/.exec(message.trim());
  if (!m) return null;
  const step = STEP_LABEL_TO_KEY[m[1]];
  return step ? { step, outcome: m[2] as 'done' | 'failed' } : null;
}

type JobCenterContextValue = {
  /** Most-recently-created first, capped at MAX_JOBS. */
  jobs: JobRecord[];
  activeCount: number;
  unreadFailedCount: number;
  /** True until the first hydration fetch resolves (success or failure). */
  hydrating: boolean;
  /** True while the live SSE feed is connected; false while on the polling fallback. */
  connected: boolean;
  /** True while the browser reports no network — the feed is paused, not broken. */
  offline: boolean;
  /** True when the last full list fetch failed and we have nothing to show. */
  hydrateError: boolean;
  /** Manual re-fetch of the job list (the retry affordance for hydrateError). */
  refresh: () => Promise<void>;
  getJob: (jobId: string) => JobRecord | undefined;
  /** Latest progress/ETA seen for a job (from job_events, not the job row itself). */
  getProgress: (jobId: string) => JobProgress | undefined;
  /** Live per-step outcomes for a running job, parsed from its progress feed. */
  getSteps: (jobId: string) => JobStepOutcomes | undefined;
  cancel: (jobId: string) => Promise<void>;
  retry: (jobId: string) => Promise<void>;
  acknowledge: (jobId: string) => void;
  acknowledgeAll: () => void;
  /** Whether a failed job has been acknowledged (drops it out of Needs attention). */
  isAcknowledged: (jobId: string) => boolean;
};

const JobCenterContext = createContext<JobCenterContextValue | null>(null);

function sortedByCreatedDesc(jobs: Iterable<JobRecord>): JobRecord[] {
  return Array.from(jobs).sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}

export function JobCenterProvider({ children }: { children: ReactNode }) {
  const [jobsMap, setJobsMap] = useState<Map<string, JobRecord>>(new Map());
  const [progressMap, setProgressMap] = useState<Map<string, JobProgress>>(new Map());
  const [stepsMap, setStepsMap] = useState<Map<string, JobStepOutcomes>>(new Map());
  const [ackedFailedIds, setAckedFailedIds] = useState<Set<string>>(new Set());
  const [hydrating, setHydrating] = useState(true);
  const [connected, setConnected] = useState(false);
  const [offline, setOffline] = useState(
    typeof navigator !== 'undefined' ? !navigator.onLine : false,
  );
  const [hydrateError, setHydrateError] = useState(false);

  // Jobs are OWNER-scoped server-side, so the client cache is per-identity by
  // definition. authKey names the identity this cache belongs to:
  //   'user:<sub>'  — an authenticated clinician
  //   'dev'         — dev IAM mode (no login required)
  //   null          — logged out / session expired: nothing may be shown
  // Any change of key clears every piece of job state (list, progress, acks,
  // event cursor) BEFORE anything is fetched for the new identity. Without
  // this, a logout left the previous clinician's job list — patient-linked
  // work — sitting on screen at the login prompt of a shared workstation.
  const { status: authStatus, profile, isDevIam } = useAuth();
  const authKey =
    authStatus === 'authenticated'
      ? `user:${profile?.sub || profile?.preferred_username || 'unknown'}`
      : isDevIam
        ? 'dev'
        : null;

  const mountedRef = useRef(true);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const lastSeqRef = useRef<number | undefined>(undefined);
  const offlineRef = useRef(offline);
  offlineRef.current = offline;

  const upsertJob = useCallback((job: JobRecord) => {
    setJobsMap((prev) => {
      const next = new Map(prev);
      next.set(job.job_id, job);
      if (next.size <= MAX_JOBS) return next;
      const capped = sortedByCreatedDesc(next.values()).slice(0, MAX_JOBS);
      return new Map(capped.map((j) => [j.job_id, j]));
    });
  }, []);

  const replaceAll = useCallback((list: JobRecord[]) => {
    setJobsMap(new Map(sortedByCreatedDesc(list).slice(0, MAX_JOBS).map((j) => [j.job_id, j])));
  }, []);

  const clearAll = useCallback(() => {
    setJobsMap(new Map());
    setProgressMap(new Map());
    setStepsMap(new Map());
    setAckedFailedIds(new Set());
    setHydrateError(false);
    lastSeqRef.current = undefined;
  }, []);

  const hydrate = useCallback(async () => {
    if (!isJobsApiAvailable()) {
      setHydrating(false);
      return;
    }
    try {
      const list = await fetchJobs({ limit: MAX_JOBS });
      if (mountedRef.current) {
        replaceAll(list);
        setHydrateError(false);
      }
    } catch {
      // A failed FIRST load must be a visible error state, not a silently
      // empty Job Center — an empty list here is exactly the moment a user
      // concludes "my job is gone". Later successful polls clear it.
      if (mountedRef.current) {
        setJobsMap((prev) => {
          if (prev.size === 0) setHydrateError(true);
          return prev;
        });
      }
    } finally {
      if (mountedRef.current) setHydrating(false);
    }
  }, [replaceAll]);

  const pollingRef = useRef(false);

  const stopPolling = useCallback(() => {
    pollingRef.current = false;
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    const tick = async () => {
      if (!mountedRef.current || !pollingRef.current) return;
      // While the browser says we're offline, polling can only fail — skip the
      // request but keep the timer alive so recovery needs no extra wiring.
      if (!offlineRef.current) {
        try {
          const list = await fetchJobs({ limit: MAX_JOBS });
          if (mountedRef.current && pollingRef.current) {
            replaceAll(list);
            setHydrateError(false);
          }
        } catch {
          // keep last known state; try again next tick
        }
      }
      if (mountedRef.current && pollingRef.current) {
        pollTimerRef.current = setTimeout(tick, POLL_FALLBACK_MS);
      }
    };
    void tick();
  }, [replaceAll]);

  // Events carry a status/progress delta, not the full row -- refetch the one
  // job they reference. The job list is small enough that a per-event fetch
  // is cheap and always correct, rather than hand-merging partial fields.
  const applyEvent = useCallback(
    (evt: JobEvent) => {
      if (evt.progress_pct != null || evt.eta_seconds != null || evt.message) {
        setProgressMap((prev) => {
          const next = new Map(prev);
          // MERGE, never replace. The three fields arrive on different
          // events: a step narration carries a message with no percentage,
          // and a checkpoint carries a percentage with no message. Replacing
          // wholesale meant each one erased the other -- a checkpoint landing
          // after a step narration blanked the live step, and the drawer fell
          // back to "Starting…" on a job that was minutes in. Last known
          // value wins per field.
          const cur = next.get(evt.job_id);
          next.set(evt.job_id, {
            progressPct: evt.progress_pct ?? cur?.progressPct ?? null,
            etaSeconds: evt.eta_seconds ?? cur?.etaSeconds ?? null,
            message: evt.message ?? cur?.message ?? null,
          });
          return next;
        });
        const parsed = parseStepMessage(evt.message);
        if (parsed) {
          setStepsMap((prev) => {
            const next = new Map(prev);
            next.set(evt.job_id, { ...(next.get(evt.job_id) ?? {}), [parsed.step]: parsed.outcome });
            return next;
          });
        }
      }
      void fetchJob(evt.job_id).then((res) => {
        if (res && mountedRef.current) upsertJob(res.job);
      });
    },
    [upsertJob],
  );

  // Ref so the network/visibility listeners can trigger an immediate reconnect
  // without re-registering; assigned by the connection effect below.
  const reconnectNowRef = useRef<() => void>(() => {});

  useEffect(() => {
    mountedRef.current = true;

    // Logged out (or session expired): show nothing, fetch nothing. State was
    // already cleared by the authKey effect below; just stay torn down.
    if (authKey === null) {
      setConnected(false);
      setHydrating(false);
      return () => {
        mountedRef.current = false;
      };
    }

    void hydrate();

    if (!isJobsApiAvailable()) {
      return () => {
        mountedRef.current = false;
      };
    }

    const controller = new AbortController();

    // The live SSE feed with a real reconnect loop. Every stream drop (server
    // restart, proxy idle timeout, network blip) degrades to polling AND
    // schedules a reconnect with exponential backoff; a successful reconnect
    // resumes from the last seen event seq (Last-Event-Id), re-hydrates the
    // list to cover anything missed while degraded, and stops the polling
    // fallback. The browser 'online' event and tab re-focus skip the backoff
    // entirely (reconnectNowRef) — a restored network reconnects in ~0s
    // instead of waiting out a 30s timer.
    const connect = () => {
      if (!mountedRef.current || controller.signal.aborted) return;
      // No network: don't burn a connect attempt that cannot succeed. The
      // 'online' listener calls reconnectNowRef the moment it returns.
      if (offlineRef.current) return;
      openJobEventStream(
        (evt) => {
          if (typeof evt.seq === 'number') lastSeqRef.current = evt.seq;
          applyEvent(evt);
        },
        {
          lastSeq: lastSeqRef.current,
          signal: controller.signal,
          onOpen: () => {
            if (!mountedRef.current || controller.signal.aborted) return;
            const wasReconnect = reconnectAttemptRef.current > 0;
            reconnectAttemptRef.current = 0;
            setConnected(true);
            stopPolling();
            if (wasReconnect) void hydrate();
          },
        },
      )
        .catch(() => {
          /* fall through to the shared degrade-and-retry path below */
        })
        .finally(() => {
          if (!mountedRef.current || controller.signal.aborted) return;
          setConnected(false);
          startPolling();
          const attempt = reconnectAttemptRef.current;
          reconnectAttemptRef.current = Math.min(attempt + 1, 8);
          const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** attempt);
          reconnectTimerRef.current = setTimeout(connect, delay);
        });
    };

    reconnectNowRef.current = () => {
      if (!mountedRef.current || controller.signal.aborted) return;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      reconnectAttemptRef.current = 0;
      connect();
      void hydrate();
    };

    connect();

    return () => {
      mountedRef.current = false;
      reconnectNowRef.current = () => {};
      controller.abort();
      stopPolling();
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
    // authKey re-keys the whole connection: logout tears down, login (or an
    // in-app clinician switch) starts clean for the new identity.
  }, [authKey, hydrate, applyEvent, startPolling, stopPolling]);

  // Identity changed (login, logout, clinician switch): the cached jobs belong
  // to someone else — drop them before anything renders for the new identity.
  const prevAuthKeyRef = useRef<string | null>(authKey);
  useEffect(() => {
    if (prevAuthKeyRef.current !== authKey) {
      prevAuthKeyRef.current = authKey;
      clearAll();
      if (authKey !== null) setHydrating(true);
    }
  }, [authKey, clearAll]);

  // Network + tab lifecycle: reconnect the instant conditions improve instead
  // of waiting out the exponential-backoff timer.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onOnline = () => {
      // The ref is the authority at event time: reconnectNow() runs
      // synchronously, before React re-renders with the new state, so
      // updating only the state would make connect() still see offline=true
      // and refuse the very reconnect this handler exists to trigger.
      offlineRef.current = false;
      setOffline(false);
      reconnectNowRef.current();
    };
    const onOffline = () => {
      offlineRef.current = true;
      setOffline(true);
      setConnected(false);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !offlineRef.current) {
        reconnectNowRef.current();
      }
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Keep the ack set and progress cache from growing unbounded over a
  // long-lived tab -- prune anything for a job that fell out of the capped list.
  useEffect(() => {
    setAckedFailedIds((prev) => {
      const next = new Set(Array.from(prev).filter((id) => jobsMap.has(id)));
      return next.size === prev.size ? prev : next;
    });
    setProgressMap((prev) => {
      const next = new Map(Array.from(prev).filter(([id]) => jobsMap.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [jobsMap]);

  const cancel = useCallback(
    async (jobId: string) => {
      await apiCancelJob(jobId);
      const res = await fetchJob(jobId);
      if (res) upsertJob(res.job);
    },
    [upsertJob],
  );

  const retry = useCallback(
    async (jobId: string) => {
      const result = await apiRetryJob(jobId);
      const [original, retried] = await Promise.all([fetchJob(jobId), fetchJob(result.job_id)]);
      if (original) upsertJob(original.job);
      if (retried) upsertJob(retried.job);
    },
    [upsertJob],
  );

  // Acknowledgement is SERVER-durable (jobs.seen_at, migration 022) with the
  // local set as an optimistic overlay: the badge clears instantly, and the
  // server stamp is what makes it survive refresh, other devices, and
  // re-login. Fire-and-forget: if the POST fails the local overlay still
  // holds for this session, and the row simply resurfaces next session —
  // an ack that silently un-acks NOW would be worse than one that repeats.
  const acknowledge = useCallback((jobId: string) => {
    setAckedFailedIds((prev) => (prev.has(jobId) ? prev : new Set(prev).add(jobId)));
    void markJobsSeen([jobId]).catch(() => {});
  }, []);

  // Must acknowledge exactly what the drawer files under "Needs review", which
  // is failures AND completed-with-warnings -- a prepare is fail-open per step,
  // so 'completed' alone never proved the clinical work landed. Filtering on
  // status === 'failed' alone is why the button did nothing on a list made
  // entirely of "Completed - needs review" rows: it collected zero ids, wrote
  // an empty set, and every card stayed exactly where it was.
  //
  // Merge into the existing set rather than replacing it. `new Set(justThese)`
  // dropped every id acknowledged earlier in the session, so a row that had
  // already been dismissed came back the moment a later ack ran.
  const acknowledgeAll = useCallback(() => {
    const ids = Array.from(jobsMap.values())
      .filter((j) => j.status === 'failed' || (j.status === 'completed' && jobHasWarnings(j)))
      .map((j) => j.job_id);
    if (ids.length === 0) return;
    setAckedFailedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
    void markJobsSeen(ids).catch(() => {});
  }, [jobsMap]);

  const jobs = useMemo(() => sortedByCreatedDesc(jobsMap.values()), [jobsMap]);
  const activeCount = useMemo(() => jobs.filter((j) => ACTIVE_STATUSES.has(j.status)).length, [jobs]);
  // Counts anything that needs a human: outright failures AND jobs that
  // completed with broken clinical steps (a prepare is fail-open per step, so
  // 'completed' alone never proved the work landed).
  const unreadFailedCount = useMemo(
    () =>
      jobs.filter(
        (j) =>
          !j.seen_at &&
          !ackedFailedIds.has(j.job_id) &&
          (j.status === 'failed' ||
            (j.status === 'completed' && jobHasWarnings(j))),
      ).length,
    [jobs, ackedFailedIds],
  );
  const getJob = useCallback((jobId: string) => jobsMap.get(jobId), [jobsMap]);
  const isAcknowledged = useCallback(
    (jobId: string) => Boolean(jobsMap.get(jobId)?.seen_at) || ackedFailedIds.has(jobId),
    [jobsMap, ackedFailedIds],
  );
  const getProgress = useCallback((jobId: string) => progressMap.get(jobId), [progressMap]);
  const getSteps = useCallback((jobId: string) => stepsMap.get(jobId), [stepsMap]);

  const value = useMemo(
    () => ({
      jobs,
      activeCount,
      unreadFailedCount,
      hydrating,
      connected,
      offline,
      hydrateError,
      refresh: hydrate,
      getJob,
      getProgress,
    getSteps,
      cancel,
      retry,
      acknowledge,
      acknowledgeAll,
      isAcknowledged,
    }),
    [
      jobs,
      activeCount,
      unreadFailedCount,
      hydrating,
      connected,
      offline,
      hydrateError,
      hydrate,
      getJob,
      getProgress,
      cancel,
      retry,
      acknowledge,
      acknowledgeAll,
      isAcknowledged,
    ],
  );

  return <JobCenterContext.Provider value={value}>{children}</JobCenterContext.Provider>;
}

export function useJobCenter(): JobCenterContextValue {
  const ctx = useContext(JobCenterContext);
  if (!ctx) {
    throw new Error('useJobCenter requires JobCenterProvider');
  }
  return ctx;
}

export function useJobCenterOptional(): JobCenterContextValue | null {
  return useContext(JobCenterContext);
}
